import { Link } from 'react-router-dom'

import type { Conversation } from '@/api/types'
import { Avatar } from '@/components/ui/Avatar'
import { PixelDigits } from '@/components/brand/PixelDigits'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'
import { useFormat } from '@/lib/format'

/** 会话列表里的一行：对方、最后一条（自己发的带「你：」）、时间、未读数。 */
export function ConversationListItem({ conversation }: { conversation: Conversation }) {
  const t = useT()
  const { relativeTime } = useFormat()
  const { other, last_message: last, unread } = conversation

  return (
    <Link
      to={`/messages/${conversation.id}`}
      className={cn(
        'flex gap-3 border-b border-line px-4 py-3 transition-colors hover:bg-surface-hover',
        unread > 0 && 'bg-brand/5',
      )}
    >
      <Avatar src={other.avatar_url} alt={other.display_name} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1 text-[15px]">
          <span className="truncate font-bold">{other.display_name}</span>
          <span className="truncate font-mono text-[14px] text-ink-muted">@{other.username}</span>
          {conversation.last_message_at && (
            <>
              <span className="text-ink-muted">·</span>
              <span className="shrink-0 font-mono text-[14px] text-ink-muted">
                {relativeTime(conversation.last_message_at)}
              </span>
            </>
          )}
          {unread > 0 && (
            <span
              className="ml-auto flex h-4 min-w-4 items-center justify-center bg-brand px-[3px] text-on-brand px-corners [--px-step:1px]"
              aria-label={t('messages.unread', { count: unread })}
            >
              <PixelDigits value={unread > 99 ? '99+' : String(unread)} />
            </span>
          )}
        </div>
        {last && (
          <p
            className={cn(
              'truncate text-[15px]',
              unread > 0 ? 'font-bold text-ink' : 'text-ink-muted',
            )}
          >
            {last.is_mine ? t('messages.youPrefix', { content: last.content }) : last.content}
          </p>
        )}
      </div>
    </Link>
  )
}
